import { DragDropModule } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatTreeModule } from "@angular/material/tree";
import { NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { InlineSVGModule } from "ng-inline-svg";
import { BasicProgressBarComponent } from "./components/basic-progress-bar/basic-progress-bar.component";
import { ButtonsComponent } from "./components/buttons/buttons.component";
import { GraphTrendComponent } from "./components/chartjs-components/graph-trend/graph-trend.component";
import { TODAY_VERTICAL_JS_ANNOTATION, VERTICAL_TODAY_ANNOTATION, CHART_JS_DEFAULT_TODAY_DATES, DEFAULT_TODAY_DATES } from "./components/chartjs-components/models/chart-js-injection-token";
import { SparklineComponent } from "./components/chartjs-components/sparkline/sparkline.component";
import { VerticalBarComponent } from "./components/chartjs-components/vertical-bar/vertical-bar.component";
import { CheckboxSearchListComponent } from "./components/checkbox-search-list/checkbox-search-list.component";
import { ControlErrorComponent } from "./components/control-error/control-error.component";
import { CronGeneratorComponent } from "./components/cron-generator/cron-generator.component";
import { DashboardPortalModule } from "./components/dashboard-portal/dashboard-portal.module";
import { DynamicWordingsModule } from "./components/dynamic-wordings/dynamic-wordings.module";
import { AnomalySettingComponent } from "./components/entity-dashboard/anomalies/anomaly-setting/anomaly-setting.component";
import { AnomalySettingsContainerComponent } from "./components/entity-dashboard/anomalies/anomaly-settings-container/anomaly-settings-container.component";
import { FiltersBarComponent } from "./components/filters-bar/filters-bar.component";
import { FromToDatePickerComponent } from "./components/from-to-date-picker/from-to-date-picker.component";
import { FromToDisplayComponent } from "./components/from-to-display/from-to-display.component";
import { TimelineMarkerDirective } from "./components/horizontal-timeline/directives/timeline-marker.directive";
import { HorizontalTimelineComponent } from "./components/horizontal-timeline/horizontal-timeline.component";
import { IconWithEllipseComponent } from "./components/icon-with-ellipse/icon-with-ellipse.component";
import { InsightResolutionsDisplayComponent } from "./components/kpi-display-components/insight-resolutions-display/insight-resolutions-display.component";
import { MultiKpiActionGraphTrendComponent } from "./components/kpi-display-components/multi-kpi-action-graph-trend/multi-kpi-action-graph-trend.component";
import { ListViewModule } from "./components/list-view/list-view.module";
import { MenuCheckboxListComponent } from "./components/menu-checkbox-list/menu-checkbox-list.component";
import { NetopTabGroupComponent } from "./components/netop-tab-group/netop-tab-group.component";
import { NetopTabComponent } from "./components/netop-tab/netop-tab.component";
import { NetopToggleComponent } from "./components/netop-toggle/netop-toggle.component";
import { PagingArrowsComponent } from "./components/paging-arrows/paging-arrows.component";
import { AllowDenyViewComponent } from "./components/policies-display/allow-deny-view/allow-deny-view.component";
import { PressedButtonComponent } from "./components/pressed-button/pressed-button.component";
import { SearchMenuBoxComponent } from "./components/search-menu-box/search-menu-box.component";
import { SearchInputComponent } from "./components/search/search.component";
import { VenuesMultiSelectListComponent } from "./components/select-list/venues-multi-select-list/venues-multi-select-list.component";
import { SelectionTreeModule } from "./components/selection-tree/selection-tree.module";
import { TimeRangeManagerComponent } from "./components/time-range-manager/time-range-manager.component";
import { TimeRangePickerModule } from "./components/time-range-picker/time-range-picker.module";
import { VenuePickerComponent } from "./components/venue-picker/venue-picker.component";
import { VerticalMenuOptionsComponent } from "./components/vertical-menu-options/vertical-menu-options.component";
import { BackupNowComponent } from "./modals/backup-now/backup-now.component";
import { CloneDialogComponent } from "./modals/clone-dialog/clone-dialog.component";
import { KpisTrendsComponent } from "./modals/kpis-trends/kpis-trends.component";
import { SingleKpiDisplayComponent } from "./modals/kpis-trends/single-kpi-display/single-kpi-display.component";
import { NetopDialogModule } from "./modals/netop-dialog/netop-dialog.module";
import { PeriodicBackupComponent } from "./modals/periodic-backup/periodic-backup.component";
import { RestoreComponent } from "./modals/restore/restore.component";
import { ScheduleDialogComponent } from "./modals/schedule-dialog/schedule-dialog.component";
import { TenantPeriodicBackupComponent } from "./modals/tenant-periodic-backup/tenant-periodic-backup.component";
import { NgxSelectModule } from "./ngx-select/ngx-select.module";
import { SharedDirectivesModule } from "./shared-directives.module";
import { SharedPipesModule } from "./shared-pipes.module";
import { NbInputModule, NbSelectModule, NbOptionModule, NbFormFieldModule, NbButtonModule, NbAutocompleteModule, NbIconModule, NbTooltipModule } from "@nebular/theme";


const components = [
  HorizontalTimelineComponent,
  SparklineComponent,
  VerticalBarComponent,
  KpisTrendsComponent,
  MultiKpiActionGraphTrendComponent,
  GraphTrendComponent,
  CronGeneratorComponent,
  ButtonsComponent,
  CheckboxSearchListComponent,
  IconWithEllipseComponent,
  AnomalySettingComponent,
  PressedButtonComponent,
  MenuCheckboxListComponent,
  BasicProgressBarComponent,
  NetopToggleComponent,
  FiltersBarComponent,
  FromToDisplayComponent,
  AllowDenyViewComponent,
  PeriodicBackupComponent,
  BackupNowComponent,
  RestoreComponent,
  CloneDialogComponent,
  TenantPeriodicBackupComponent,
  InsightResolutionsDisplayComponent,
  NetopTabGroupComponent,
  NetopTabComponent,
  SingleKpiDisplayComponent,
  TimeRangeManagerComponent,
  FromToDatePickerComponent,
  PagingArrowsComponent,
  VenuesMultiSelectListComponent,
  ScheduleDialogComponent,
  VerticalMenuOptionsComponent,
  SearchMenuBoxComponent,
  ControlErrorComponent,
  AnomalySettingsContainerComponent,
  VenuePickerComponent
];

const modules = [
  MatIconModule,
  InlineSVGModule,
  SharedPipesModule,
  SharedDirectivesModule,
  MatTooltipModule,
  MatSlideToggleModule,
  TranslateModule,
  FormsModule,
  ReactiveFormsModule,
  MatSelectModule,
  MatInputModule,
  NetopDialogModule,
  MatButtonModule,
  MatDialogModule,
  MatRadioModule,
  MatCheckboxModule,
  MatListModule,
  MatOptionModule,
  MatButtonToggleModule,
  MatToolbarModule,
  NgbTooltipModule,
  SharedDirectivesModule,
  MatMenuModule,
  MatProgressSpinnerModule,
  MatAutocompleteModule,
  DashboardPortalModule,
  MatTabsModule,
  MatDatepickerModule,
  MatFormFieldModule,
  NgxSelectModule,
  DragDropModule,
  MatTreeModule,
  ListViewModule,
  DynamicWordingsModule,
  NbInputModule,
  NbSelectModule,
  NbOptionModule,
  NbFormFieldModule,
  NbButtonModule,
  NbAutocompleteModule,
  NbIconModule,
  NbTooltipModule,
];

@NgModule({
  declarations: [
    components,
    TimelineMarkerDirective,
  ],
  imports: [
    CommonModule,
    TimeRangePickerModule,
    SearchInputComponent,
    modules
  ],
  exports: [
    components,
    SelectionTreeModule,
    SearchInputComponent,
    TimeRangePickerModule,
    DynamicWordingsModule
  ],
  providers: [
    {
      provide: TODAY_VERTICAL_JS_ANNOTATION,
      useValue: VERTICAL_TODAY_ANNOTATION
    },
    {
      provide: CHART_JS_DEFAULT_TODAY_DATES,
      useValue: DEFAULT_TODAY_DATES
    },
  ]
})
export class SharedComponentsModule {
}
